import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import type {ComponentPropsWithoutRef, ReactNode} from 'react'
import {forwardRef} from 'react'

import {CELL_HEIGHT} from '../react_table/constants'
import styles from './omnibar-container.module.css'

type Props = {
  horizontalScrollbarSize: number | null
  verticalScrollbarSize?: number | null
  scrollbarSize?: number | null
  disableBlur?: boolean
  isFixed?: boolean | null
  effectiveTableHeight?: number | null
  children?: ReactNode
}

const getStyles = ({
  verticalScrollbarSize,
  isFixed,
  horizontalScrollbarSize,
  effectiveTableHeight,
  disableBlur,
}: Pick<
  Props,
  'isFixed' | 'verticalScrollbarSize' | 'horizontalScrollbarSize' | 'effectiveTableHeight' | 'disableBlur'
>): React.CSSProperties => {
  if (isFixed) {
    return {
      '--omnibar-horizontal-scrollbar-size': `${horizontalScrollbarSize ?? 0}px`,
      backdropFilter: disableBlur ? 'unset' : 'blur(12px)',
      paddingBottom: horizontalScrollbarSize ? 0 : `12px`,
      position: 'absolute',
      bottom: horizontalScrollbarSize ? `${horizontalScrollbarSize}px` : 0,
      paddingRight: verticalScrollbarSize ? 0 : '8px',
    }
  }
  return {
    '--omnibar-horizontal-scrollbar-size': `${horizontalScrollbarSize ?? 0}px`,
    backgroundColor: 'var(--bgColor-default)',
    paddingBottom: '1px',
    position: 'absolute',
    top: `${effectiveTableHeight}px`,
    height: `${CELL_HEIGHT}px`,
    borderBottom: '1px solid',
    borderBottomColor: 'var(--borderColor-default)',
    boxShadow: 'var(--shadow-resting-small)',
  }
}

export const OmnibarContainer = forwardRef<HTMLDivElement, Props & ComponentPropsWithoutRef<'div'>>(
  (
    {
      horizontalScrollbarSize,
      disableBlur,
      verticalScrollbarSize,
      isFixed,
      effectiveTableHeight,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        {...props}
        ref={ref}
        className={clsx(className, styles.OmnibarContainer)}
        style={getStyles({isFixed, verticalScrollbarSize, horizontalScrollbarSize, effectiveTableHeight, disableBlur})}
        {...testIdProps('omnibar-container')}
      >
        {children}
      </div>
    )
  },
)

OmnibarContainer.displayName = 'OmnibarContainer'
